import classNames from 'classnames/bind';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import Heading from 'components/shared/heading';
import useDeviceType, { DEVICE_TYPE } from 'hooks/use-device-type';

import styles from './how-it-works.module.scss';

const cx = classNames.bind(styles);

const DATA = [
  {
    title: 'Install <br/> the app',
    description: `Installation on your machine of choice (macOS or Windows) is as easy as visiting our download link and dragging the application to your desktop!`,
  },
  {
    title: 'create <br/> a profile',
    description:
      'Plug in your shipping and billing information with a few easy steps, or import existing profiles with the click of a button.',
  },
  {
    title: 'create <br/> a task',
    description: 'Ready your tasks for checkout in seconds with our intuitive task creation tab.',
  },
  {
    title: 'Track Task <br/>Status',
    description:
      'Follow your tasks from idle to success on the Task Dashboard tab or track cart and checkout numbers on the Analytics tab.',
  },
];

const SPOTLIGHT_RADIUS_PX = 450;
const START_POSITION_REM = [5, 32];

const HowItWorks = () => {
  const howToAnimationRef = React.useRef();

  const [sectionRef, inView] = useInView({ rootMargin: '40px' });

  const [startPosition, setStartPosition] = React.useState([0, 0]);

  const deviceType = useDeviceType();

  const disableSpotlightAnimation = deviceType !== DEVICE_TYPE.desktop;

  React.useEffect(() => {
    if (disableSpotlightAnimation) {
      return;
    }
    if (!howToAnimationRef.current) {
      return;
    }
    if (inView && howToAnimationRef.current) {
      howToAnimationRef.current.move(...startPosition);
      howToAnimationRef.current.resume();
    } else {
      howToAnimationRef.current.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, startPosition, deviceType]);

  React.useEffect(() => {
    if (disableSpotlightAnimation) {
      return;
    }

    const remToPx = (rem) => {
      return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    };

    const section = document.getElementById('how-it-works');
    const sectionTopOffset = window.pageYOffset + section.getBoundingClientRect().top;
    const startX = remToPx(START_POSITION_REM[0]);
    const startY = remToPx(START_POSITION_REM[1]) + sectionTopOffset;

    // eslint-disable-next-line no-use-before-define
    howToAnimationRef.current = createSpotlightAnimation(section);
    if (!howToAnimationRef.current) {
      return;
    }
    howToAnimationRef.current.move(startX, startY);

    setStartPosition([startX, startY]);

    return () => {
      howToAnimationRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType]);

  const removeBreakOnMobile = (title) =>
    deviceType === DEVICE_TYPE.mobile.portrait ? title : title.replace('<br/> ', '');

  return (
    <section className={cx('container', 'wrapper')} ref={sectionRef} id="how-it-works">
      {!disableSpotlightAnimation && (
        <>
          <div className={cx('bg-moving', inView ? 'bg-moving-1' : undefined)} />
          <div className={cx('bg-moving', inView ? 'bg-moving-2' : undefined)} />

          <div className={cx('bg-title')} />

          <div className={cx('bg-cards')}>
            {DATA.map((item, index) => (
              <div key={`bg-item-${index}`} className={cx('bg-cards-item')} />
            ))}
          </div>
        </>
      )}

      {!disableSpotlightAnimation && (
        <div id="how-to-spotlight">
          <svg
            id="how-to-spotlight-svg"
            viewBox={`0 0 ${SPOTLIGHT_RADIUS_PX * 2} ${SPOTLIGHT_RADIUS_PX * 2}`}
          >
            <defs>
              <radialGradient id="grad2" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" style={{ stopColor: 'rgb(204, 255, 51)', stopOpacity: 0 }} />
                <stop offset="9%" style={{ stopColor: 'rgb(204, 255, 51)', stopOpacity: 0 }} />
                <stop offset="9%" style={{ stopColor: 'rgb(255, 255, 255)', stopOpacity: 1 }} />
              </radialGradient>
            </defs>
            <rect
              id="how-to-spotlight-rect"
              x={SPOTLIGHT_RADIUS_PX}
              y={SPOTLIGHT_RADIUS_PX}
              width={3 * SPOTLIGHT_RADIUS_PX}
              height={3 * SPOTLIGHT_RADIUS_PX}
              fill="url(#grad2)"
            />
          </svg>
        </div>
      )}

      <div className={cx('title-wrapper')}>
        <Heading className={cx('title')} size="lg" tag="h2">
          how it works
        </Heading>
      </div>
      <div className={cx('tasks')}>
        {DATA.map(({ title, description }, i) => {
          return (
            <div className={cx('task-card')} key={title}>
              {deviceType !== DEVICE_TYPE.mobile.portrait && (
                <span className={cx('number')}>{String(i + 1).padStart(2, '0')}.</span>
              )}
              <Heading
                className={cx('task-title')}
                tag="h3"
                size="md"
                innerHTML={`${
                  deviceType === DEVICE_TYPE.mobile.portrait
                    ? `${String(i + 1).padStart(2, '0')}. `
                    : ''
                }${removeBreakOnMobile(title)}`}
              />
              <p className={cx('description')}>{description}</p>
              {!i ? (
                <>
                  <a className={cx('button')} href="/">
                    NOT AVAILABLE
                  </a>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default HowItWorks;

const createSpotlightAnimation = (sectionEl) => {
  const spotlight = document.getElementById('how-to-spotlight');
  if (!spotlight) {
    return;
  }
  const spotlightSvg = document.getElementById('how-to-spotlight-svg');
  const spotlightRect = document.getElementById('how-to-spotlight-rect');

  const width = document.documentElement.clientWidth;
  const height = document.documentElement.clientHeight;

  const moveSpotlight = (gx, gy) => {
    const rect = sectionEl.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const sectionTopOffset = rect.top + scrollTop;

    const spotlightContainerSize = 3.5 * Math.max(width, height);
    spotlight.style.left = `${gx - SPOTLIGHT_RADIUS_PX - spotlightContainerSize / 2}px`;
    spotlight.style.top = `${
      gy - sectionTopOffset - SPOTLIGHT_RADIUS_PX - spotlightContainerSize / 2
    }px`;
    spotlight.style.width = `${spotlightContainerSize}px`;
    spotlight.style.height = `${spotlightContainerSize}px`;

    spotlightRect.setAttribute('width', `${spotlightContainerSize}`);
    spotlightRect.setAttribute('height', `${spotlightContainerSize}`);
    spotlightSvg.setAttribute('viewBox', `0 0 ${spotlightContainerSize} ${spotlightContainerSize}`);
  };

  const onMouseMove = (e) => {
    let gx = 0;
    let gy = 0;

    if (e.pageX || e.pageY) {
      gx = e.pageX;
      gy = e.pageY;
    } else if (e.clientX || e.clientY) {
      gx = e.clientX + document.body.scrollLeft;
      gy = e.clientY + document.body.scrollTop;
    }
    moveSpotlight(gx, gy);
  };

  const pause = () => {
    document.removeEventListener('mousemove', onMouseMove);
  };

  const resume = () => {
    document.addEventListener('mousemove', onMouseMove, false);
  };

  return {
    pause,
    resume,
    move: moveSpotlight,
  };
};
